import { useEffect, useMemo, useRef } from "react";
import { MatchSorterOptions } from "match-sorter";
import { MultiSelectMenu } from "./MultiSelectMenu";
import {
  MultiSelectMenuHandle,
  MultiSelectMenuProps,
  MultiSelectMenuOption,
} from "./types";
import { CheckState } from "../../../types";
import {
  SyncFilterFn,
  defaultSyncFilter,
  toSyncFilterFn,
} from "../../../internal/functions/syncFilterUtils";

export type MultiSelectMenuSyncProps = Omit<
  MultiSelectMenuProps,
  | "loadOptions"
  | "lazy"
  | "debounceMs"
  | "cache"
  | "initialLoad"
  | "selectAll"
  | "selectFiltered"
> & {
  /**
   * The options to display in the multi-select menu.
   */
  options: MultiSelectMenuOption[];
  /**
   * Controls how options are filtered and sorted when the user types a search value.
   * Can be a function that returns options in the desired display order,
   * or a MatchSorterOptions object to customize the default match-sorter behavior.
   *
   * By default, options are filtered by `label` and `searchText` using match-sorter.
   */
  filter?:
    | SyncFilterFn<MultiSelectMenuOption>
    | MatchSorterOptions<MultiSelectMenuOption>;
  /**
   * Enables the "Select All" option at the top of the list.
   * Can be a boolean to enable with default label, or an object to customize the label.
   * Click handling and check state are managed automatically.
   */
  selectAll?: boolean | { label?: string | ((checked: boolean) => string) };
  /**
   * Enables the "Select Filtered" option when a search term is active.
   * Can be a boolean to enable with default label, or a function returning a config with a custom label.
   * Click handling and check state are managed automatically.
   */
  selectFiltered?: boolean | ((searchValue: string) => { label?: string });
};

/**
 * MultiSelectMenuSync is a simplified version of MultiSelectMenu for client-side option sets.
 *
 * Features:
 * - Accepts `options` instead of `loadOptions` and `lazy`
 * - Performs client-side filtering of the options
 * - Simplified `selectAll` prop that automatically handles click and check state
 * - Simplified `selectFiltered` prop that automatically selects/deselects filtered options
 * - Supports all other props of MultiSelectMenu
 *
 * @example
 * <MultiSelectMenuSync
 *   trigger={(props) => <Button {...props}>Pick books</Button>}
 *   label="Books"
 *   options={bookOptions}
 *   value={selectedOptions}
 *   onSelectedOptionsChange={setSelectedOptions}
 *   selectAll
 * />
 */
export const MultiSelectMenuSync = (props: MultiSelectMenuSyncProps) => {
  const {
    options,
    filter: filterProp = defaultSyncFilter,
    selectAll: selectAllProp,
    selectFiltered: selectFilteredProp,
    value,
    onSelectedOptionsChange,
    ...rest
  } = props;

  const filter = useMemo(() => toSyncFilterFn(filterProp), [filterProp]);

  const selectAllConfig = useMemo(() => {
    if (!selectAllProp) return undefined;

    const enabledOptions = options.filter((opt) => !opt.disabled);
    const enabledIds = new Set(enabledOptions.map((opt) => opt.id));
    const selectedCount = value.filter((v) => enabledIds.has(v.id)).length;
    const totalCount = enabledOptions.length;
    const allSelected = selectedCount === totalCount;

    const labelProp =
      typeof selectAllProp === "object" ? selectAllProp.label : undefined;
    const label =
      typeof labelProp === "function" ? labelProp(allSelected) : labelProp;

    const checkState: CheckState = allSelected
      ? "checked"
      : selectedCount > 0
        ? "indeterminate"
        : "unchecked";

    const handleClick = () => {
      if (allSelected) {
        onSelectedOptionsChange([]);
      } else {
        onSelectedOptionsChange(enabledOptions);
      }
    };

    return {
      label,
      onClick: handleClick,
      checkState,
    };
  }, [selectAllProp, options, value, onSelectedOptionsChange]);

  const selectFilteredConfig = useMemo(() => {
    if (!selectFilteredProp) return undefined;

    return (searchValue: string) => {
      const filteredOptions = filter(options, searchValue);
      const enabledFilteredOptions = filteredOptions.filter(
        (opt) => !opt.disabled,
      );
      const selectedIds = new Set(value.map((v) => v.id));
      const allFilteredSelected =
        enabledFilteredOptions.length > 0 &&
        enabledFilteredOptions.every((opt) => selectedIds.has(opt.id));
      const someFilteredSelected = enabledFilteredOptions.some((opt) =>
        selectedIds.has(opt.id),
      );

      const label =
        typeof selectFilteredProp === "function"
          ? selectFilteredProp(searchValue).label
          : undefined;

      const checkState: CheckState = allFilteredSelected
        ? "checked"
        : someFilteredSelected
          ? "indeterminate"
          : "unchecked";

      const handleClick = () => {
        if (allFilteredSelected) {
          const filteredIds = new Set(enabledFilteredOptions.map((o) => o.id));
          onSelectedOptionsChange(value.filter((v) => !filteredIds.has(v.id)));
        } else {
          const currentIds = new Set(value.map((v) => v.id));
          const newSelections = enabledFilteredOptions.filter(
            (opt) => !currentIds.has(opt.id),
          );
          onSelectedOptionsChange([...value, ...newSelections]);
        }
      };

      return {
        label,
        onClick: handleClick,
        checkState,
      };
    };
  }, [selectFilteredProp, options, value, onSelectedOptionsChange, filter]);

  const fieldRef = useRef<MultiSelectMenuHandle>(null);
  const prevOptionsRef = useRef(options);

  useEffect(() => {
    if (prevOptionsRef.current !== options) {
      prevOptionsRef.current = options;
      fieldRef.current?.invalidate();
    }
  }, [options]);

  return (
    <MultiSelectMenu
      ref={fieldRef}
      lazy={false}
      loadOptions={(searchValue) => filter(options, searchValue)}
      debounceMs={0}
      value={value}
      onSelectedOptionsChange={onSelectedOptionsChange}
      selectAll={selectAllConfig}
      selectFiltered={selectFilteredConfig}
      {...rest}
    />
  );
};
